// src/services/creditService.js
import { toast } from 'react-toastify';
import { apiRequestSimple } from '../utils/apiHelper';

class CreditService {
  static get apiBase() {
    return window.APP_CONFIG?.apiUrl || 'https://api.dpg.net.tr';
    //return process.env.REACT_APP_API_URL || 'https://localhost:44330';
  }

  // Create a new payment guarantee request
  static createCreditRequest = async (creditData) => {
    console.log('[CreditService] createCreditRequest called with data:', creditData);
    
    const result = await apiRequestSimple('/api/credit/request', {
      method: 'POST',
      body: JSON.stringify(creditData)
    });

    if (!result.success) {
      // If logout was triggered, don't show error toast
      if (result.requiresLogout) {
        return result;
      }
      
      // Build detailed error message
      let errorMessage = result.message || 'Credit request could not be created';
      if (result.error?.data?.error) {
        errorMessage += `: ${result.error.data.error}`;
      }
      if (result.error?.data?.innerError && !errorMessage.includes(result.error.data.innerError)) {
        errorMessage += ` (${result.error.data.innerError})`;
      }
      
      toast.error(errorMessage);
      return {
        success: false,
        message: errorMessage,
        error: result.error?.data?.error,
        innerError: result.error?.data?.innerError,
        status: result.status
      };
    }

    toast.success(result.message || 'Credit request created successfully');
    return {
      success: true,
      data: result.data,
      message: result.message
    };
  };

  // Get credits by buyer ID
  static getCreditsByBuyer = async (buyerId) => {
    const result = await apiRequestSimple(`/api/credit/getAllByBuyer?buyerId=${buyerId}`, {
      method: 'GET',
    });

    if (!result.success) {
      if (result.requiresLogout) {
        return result;
      }
      return {
        success: false,
        message: result.message || 'Credits could not be retrieved',
        data: []
      };
    }

    return {
      success: true,
      data: result.data || [] // API returns array directly, not wrapped in data property
    };
  };

  // Get credits by seller ID
  static getCreditsBySeller = async (sellerId) => {
    const result = await apiRequestSimple(`/api/credit/getAllBySeller?sellerId=${sellerId}`, {
      method: 'GET',
    });

    if (!result.success) {
      if (result.requiresLogout) {
        return result;
      }
      return {
        success: false,
        message: result.message || 'Credits could not be retrieved',
        data: []
      };
    }

    return {
      success: true,
      data: result.data || [] // API returns array directly, not wrapped in data property
    };
  };

  // Get previous sellers that the user has created payment guarantees for
  static getPreviousSellers = async () => {
    const result = await apiRequestSimple('/api/credit/previous-sellers', {
      method: 'GET',
    });

    if (!result.success) {
      if (result.requiresLogout) {
        return result;
      }
      return {
        success: false,
        message: result.message || 'Previous sellers could not be retrieved',
        data: []
      };
    }

    return {
      success: true,
      data: result.data || []
    };
  };

  // Process credit review (approve/reject)
  static processReview = async (reviewData) => {
    const result = await apiRequestSimple('/api/credit/processReview', {
      method: 'POST',
      body: JSON.stringify(reviewData)
    });

    if (!result.success) {
      if (result.requiresLogout) {
        return result;
      }
      toast.error(result.message || 'Operation failed');
      return {
        success: false,
        message: result.message || 'Operation failed'
      };
    }

    toast.success(result.message || 'Operation completed successfully');
    return {
      success: true,
      data: result.data,
      message: result.message
    };
  };

  // Transfer credit to another seller (Transfer Receivable)
  static transferCredit = async (transferData) => {
    // Debug: Gönderilen veriyi logla
    console.log('[CreditService] transferCredit called with:', JSON.stringify(transferData, null, 2));
    
    const result = await apiRequestSimple('/api/credit/transfer', {
      method: 'POST',
      body: JSON.stringify(transferData)
    });

    // Debug: Backend'den gelen response'u logla
    console.log('[CreditService] transferCredit response:', result);
    if (!result.success) {
      console.error('[CreditService] Transfer failed:', {
        message: result.message,
        error: result.error,
        status: result.status,
        fullResponse: result.fullResponse
      });
      
      // Backend'den gelen detaylı hata bilgilerini logla
      if (result.error?.data) {
        console.error('[CreditService] Backend error details:', {
          message: result.error.data.message,
          innerException: result.error.data.innerException,
          stackTrace: result.error.data.stackTrace
        });
      }
      
      if (result.requiresLogout) {
        return result;
      }
      
      // Detaylı hata mesajı göster
      const errorMessage = result.error?.data?.innerException 
        ? `${result.message || 'Transfer failed'}\n\nDetails: ${result.error.data.innerException}`
        : result.message || 'Transfer failed';
      
      toast.error(errorMessage);
      return {
        success: false,
        message: result.message || 'Transfer failed',
        error: result.error,
        innerException: result.error?.data?.innerException,
        stackTrace: result.error?.data?.stackTrace
      };
    }

    toast.success(result.message || 'Credit transferred successfully');
    return {
      success: true,
      data: result.data,
      message: result.message
    };
  };

  // Get transfer history for a credit
  static getTransferHistory = async (creditId) => {
    const result = await apiRequestSimple(`/api/credit/transfer-history/${creditId}`, {
      method: 'GET',
    });

    if (!result.success) {
      if (result.requiresLogout) {
        return result;
      }
      return {
        success: false,
        message: result.message || 'Transfer history could not be retrieved',
        data: []
      };
    }

    return {
      success: true,
      data: result.data || []
    };
  };

  // Get credits that were transferred by the current user (seller)
  static getTransferredCreditsBySeller = async () => {
    const result = await apiRequestSimple('/api/credit/getTransferredCreditsBySeller', {
      method: 'GET',
    });

    if (!result.success) {
      if (result.requiresLogout) {
        return result;
      }
      return {
        success: false,
        message: result.message || 'Transferred credits could not be retrieved',
        data: []
      };
    }

    return {
      success: true,
      data: result.data || []
    };
  };
}

export default CreditService;
